import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Observable, map, startWith, catchError, of } from 'rxjs';
import { ScholarshipApiService } from '../../../core/api-services/scholarship-api.service';
import { Scholarship } from '../../../core/models/scholarship';
import { ScholarshipCategory } from '../../../core/models/scholarship-category';
import { ScholarshipsState } from '../../../core/interfaces/scholarships-state';

@Component({
  selector: 'app-scholarships',
  templateUrl: './scholarships.html',
  styleUrls: ['./scholarships.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule]
})
export class ScholarshipsPage implements OnInit {
  private readonly scholarshipService = inject(ScholarshipApiService);
  private readonly router = inject(Router);

  categories: ScholarshipCategory[] = [];
  currentDate = new Date();
  state$!: Observable<ScholarshipsState>;

  constructor(private route: ActivatedRoute) { }

  ngOnInit(): void {
    // acceptingOnly keeps scholarships whose applications have closed out of the
    // public catalogue: the closing date, or an administrator override, decides.
    this.state$ = this.scholarshipService.getScholarships(true).pipe(
      map(scholarships => ({
        scholarships,
        filteredScholarships: this.groupByCategory(scholarships),
        isLoading: false,
        error: null
      })),
      startWith({
        scholarships: [],
        filteredScholarships: {},
        isLoading: true,
        error: null
      }),
      catchError(err => {
        console.error('Error loading scholarships:', err);
        return of({
          scholarships: [],
          filteredScholarships: {},
          isLoading: false,
          error: 'Failed to load scholarships. Please try again later.'
        });
      })
    );
  }

  groupByCategory(scholarships: Scholarship[]): { [key: string]: Scholarship[] } {
    const result: { [key: string]: Scholarship[] } = {};
    this.categories = [];

    scholarships.forEach(scholarship => {
      const category = scholarship.category;

      // group scholarships
      if (!result[category.id]) {
        result[category.id] = [];

        // add category only once (key moment)
        this.categories.push(category);
      }
      result[category.id].push(scholarship);
    });

    return result;
  }

  viewDetails(id: number): void {
    // older navigation method – kept for programmatic cases
    this.router.navigate(['/scholarships', id]);
  }

  subscribe(email: string): void {
    if (email) {
      console.log('Subscribing email:', email);
    }
  }

  // helpers used by *ngFor trackBy to avoid unnecessary DOM churn

  trackByCategory(index: number, category: ScholarshipCategory): number {
    return category.id;
  }

  trackByScholarship(index: number, scholarship: Scholarship): number {
    return scholarship.id;
  }

  /** default banner used when scholarship doesn't provide one */
  private readonly defaultImage = 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60';

  /** return an image URL for the card; fall back to placeholder */
  getImageUrl(s: Scholarship): string {
    return s.imageUrl || this.defaultImage;
  }
  getCategoryLabel(categoryKey: string): string {
    // const category = ScholarshipCategories.find(c => c.key === categoryKey);
    // return category ? category.label : categoryKey;
    return '';
  }
}
